import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { LoaderCircle } from 'lucide-react';

import { cn } from '../../lib/cn';
import { Tooltip } from '../Tooltip';
import styles from './IconButton.module.css';

export type IconButtonSize = 'sm' | 'md';
export type IconButtonTone = 'neutral' | 'danger' | 'primary';

export type IconButtonProps = {
  /** Обязателен по требованиям доступности (docs/06 §4). */
  readonly label: string;
  readonly icon: ReactNode;
  readonly size?: IconButtonSize;
  readonly tone?: IconButtonTone;
  readonly tooltip?: string;
  readonly loading?: boolean;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'aria-label' | 'children'>;

export function IconButton({
  label,
  icon,
  size = 'md',
  tone = 'neutral',
  tooltip,
  loading = false,
  disabled,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const button = (
    <button
      {...rest}
      type={type}
      aria-label={label}
      title={tooltip === undefined ? undefined : tooltip}
      disabled={disabled || loading}
      aria-busy={loading}
      className={cn(styles['button'], styles[size], styles[tone])}
    >
      {loading ? <LoaderCircle aria-hidden className={styles['spinner']} size={16} /> : icon}
    </button>
  );

  if (!tooltip) {
    return button;
  }
  return <Tooltip content={tooltip}>{button}</Tooltip>;
}
